import * as React from 'react';
import { isEmpty } from 'lodash';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { Form } from 'components/form';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import ActionButton from 'components/buttons/action-button/ActionButton';

import CampaignPayoutsVariablePaymentsBudget from './CampaignPayoutsVariablePaymentsBudget';
import CampaignPayoutsVariablePaymentsModalBody from './body/CampaignPayoutsVariablePaymentsModalBody';

import { __t, applyTranslations } from 'utils/language';
import { isVariablePaymentNew, populateEmptyFieldsForVariablePayment } from 'utils/payouts';

import './campaign-payouts-variable-payments-modal.scss';

import type {
  PaymentCampaignBudget,
  PaymentInfo,
  SubcampaignInfluencerProfile,
  TextEntry,
  VariablePayment
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./campaign-payouts-variable-payments-modal.i18n.json'));

type Props = {
  campaignName: string,
  campaignBudgetInfo: PaymentCampaignBudget,
  subcampaignId: number,
  influencerProfile: SubcampaignInfluencerProfile,
  variablePayments: Array<VariablePayment>,
  paymentInfo: PaymentInfo,
  otherArrangement: boolean,
  isLoading: boolean,
  isMultiple?: boolean,
  onCloseModal: () => void,
  onCreateVariablePayments: (b: number, a: Array<VariablePayment>) => void,
  onEditVariablePayments: (b: number, a: Array<VariablePayment>) => void
};

type State = {
  remainingBudget: number,
  variablePayments: Array<VariablePayment>,
  deletedVariablePayments: Array<VariablePayment>,
  isModified: boolean
};

const msgKey = 'campaign_payouts_variable_payments_modal';

class CampaignPayoutsVariablePaymentsModal extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const variablePayments = props.variablePayments || [];

    this.state = {
      remainingBudget: this.getDefaultRemainingBudget(),
      variablePayments: !isEmpty(variablePayments)
        ? variablePayments
        : [{ ...populateEmptyFieldsForVariablePayment(this.props.subcampaignId, this.props.influencerProfile.id) }],
      deletedVariablePayments: [],
      isModified: false
    };
  }

  getDefaultRemainingBudget = () => (this.props.campaignBudgetInfo || {}).remaining || 0;

  handleFieldAdd = (): void => {
    const { subcampaignId, influencerProfile } = this.props;

    this.setState(state => ({
      variablePayments: [
        ...state.variablePayments,
        { ...populateEmptyFieldsForVariablePayment(subcampaignId, influencerProfile.id) }
      ],
      isModified: true
    }));
  };

  handleFieldRemove = (id: number): void => {
    this.setState(state => {
      // $FlowIgnore - Cannot assign object literal to `removedItem` because inexact object literal is incompatible with exact `VariablePayment`
      let removedItem: VariablePayment | undefined;

      const variablePayments = state.variablePayments.filter((item) => {
        if (item.id === id) {
          removedItem = item;
        }

        return item.id !== id;
      });
      const variablePaymentsMarkedToDelete = state.variablePayments
        .filter((item) => item.id === id && !isVariablePaymentNew(item));

      return {
        remainingBudget: state.remainingBudget + (removedItem?.amount || 0),
        variablePayments,
        deletedVariablePayments: variablePaymentsMarkedToDelete
          .concat(state.deletedVariablePayments)
          .map(item => ({
            ...item,
            isDeleted: true
          })),
        isModified: true
      };
    });
  };

  handleFieldChange = (event: SyntheticEvent<TextEntry>): void => {
    const { variablePayments } = this.state;
    const target = event.currentTarget;
    const name = target.name || '';
    const value = target.value || '';
    const id = Number(target.dataset.id) || 0;

    // COLLAB-9276
    let amount: string | number = (name === 'amount' && Number(value)) || 0;
    amount = amount === 0 ? '' : amount;

    this.setState(state => {
      let oldAmount = 0;

      // $FlowIgnore - Cannot assign `variablePayments.map(...)` to `updatedVariablePayments` because property is missing in `VariablePayment` but exists in object literal
      const updatedVariablePayments: Array<VariablePayment> = variablePayments.map((item: VariablePayment) => {
        if (item.id === id) {
          oldAmount = item.amount;

          return {
            ...item,
            // $FlowIgnore - Flow is confused about allowing numbers and/or strings even though the types are correct
            [name]: name === 'amount' ? amount : value
          };
        }

        return item;
      });

      return ({
        remainingBudget: name === 'amount'
          ? state.remainingBudget + oldAmount - Number(value)
          : state.remainingBudget,
        variablePayments: updatedVariablePayments,
        isModified: true
      });
    });
  };

  handleSubmit = (): void => {
    const {
      variablePayments: initialVariablePayments,
      subcampaignId,
      onCreateVariablePayments,
      onEditVariablePayments,
      onCloseModal
    } = this.props;

    const { variablePayments, deletedVariablePayments } = this.state;
    const updatedVariablePayments = variablePayments.map(item => ({
      ...item,
      id: isVariablePaymentNew(item) ? null : item.id
    }));

    if (isEmpty(initialVariablePayments)) {
      onCreateVariablePayments(subcampaignId, updatedVariablePayments);
    } else {
      onEditVariablePayments(subcampaignId, updatedVariablePayments.concat(deletedVariablePayments));
    }

    onCloseModal();
  };

  renderBudgetInfo(): React.ReactNode {
    const { campaignBudgetInfo } = this.props;
    const { remainingBudget } = this.state;
    const totalBudget = campaignBudgetInfo.total || 0;

    return (
      <CampaignPayoutsVariablePaymentsBudget
        totalBudget={totalBudget}
        remainingBudget={remainingBudget}
      />
    );
  }

  renderButtons(): React.ReactNode {
    const { onCloseModal } = this.props;
    const commonKey = 'collabary_common';

    return (
      <ModalFrameFooter>
        <div className="campaign-payouts-variable-payments-modal__buttons">
          <ActionButton
            color="tertiary"
            onClick={onCloseModal}
          >
            {__t(`${commonKey}.action_button.cancel`)}
          </ActionButton>
          <ActionButton type="submit">
            {__t(`${commonKey}.action_button.save`)}
          </ActionButton>
        </div>
      </ModalFrameFooter>
    );
  }

  renderBody(): React.ReactNode {
    const { influencerProfile, paymentInfo, otherArrangement, subcampaignId, isLoading } = this.props;
    const { variablePayments } = this.state;

    return (
      <CampaignPayoutsVariablePaymentsModalBody
        className="campaign-payouts-variable-payments-modal__body"
        variablePayments={variablePayments.filter(item => !item.isDeleted)}
        subcampaignId={subcampaignId}
        influencerProfile={influencerProfile}
        agreedPayment={paymentInfo.amounts.agreed}
        otherArrangement={otherArrangement}
        isLoading={isLoading}
        onFieldAdd={this.handleFieldAdd}
        onFieldRemove={this.handleFieldRemove}
        onFieldChange={this.handleFieldChange}
      />
    );
  }

  render(): React.ReactNode {
    const { campaignName, isMultiple } = this.props;
    const { isModified } = this.state;
    const title = `${__t(`${msgKey}.title.main`)}${isMultiple ? 's' : ''}`;

    return (
      <ModalFrame
        title={title}
        subtitle={__t(`${msgKey}.title.sub`, { campaignTitle: campaignName })}
        data-qa="campaign-payouts-variable-payments-modal"
      >
        <Form onSubmit={this.handleSubmit}>
          {
            isModified &&
              <PromptExit type="modal" />
          }
          <ModalFrameInner>
            <div
              className="campaign-payouts-variable-payments-modal"
              data-qa="campaign-payouts-variable-payments-modal"
            >
              {this.renderBudgetInfo()}
              {this.renderBody()}
            </div>
          </ModalFrameInner>
          {this.renderButtons()}
        </Form>
      </ModalFrame>
    );
  }
}

export default CampaignPayoutsVariablePaymentsModal;
